import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead, getRecaptchaToken } from "@/lib/api";

const INTERESTS = [
  "360° Virtual Tours",
  "Avatar Creation",
  "Multilingual Dubbing",
  "Short-Form 360° Content",
  "Interactive Video",
  "Analytics & Performance",
  "Not sure yet",
];

export const LeadCaptureModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "Not sure yet", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [calendly, setCalendly] = useState("https://calendly.com/natalie-revolstudios");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken("lead_submit");
      const res = await submitLead({ ...form, recaptcha_token });
      if (res.calendly) setCalendly(res.calendly);
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong - please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => setDone(false), 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="lead-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0c] p-6 sm:p-8 shadow-2xl shadow-black"
            data-testid="lead-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Book a free strategy call"
          >
            <button
              onClick={close}
              data-testid="lead-modal-close"
              aria-label="Close"
              className="absolute right-4 top-4 rounded-lg p-1.5 text-[#a1a1aa] transition-colors duration-200 hover:text-white hover:bg-white/5"
            >
              <X className="h-4 w-4" />
            </button>

            {done ? (
              <div className="py-6 text-center" data-testid="lead-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#ed4609]" />
                <h3 className="mt-4 text-xl font-bold text-white">You're in.</h3>
                <p className="mt-2 text-sm text-[#a1a1aa]">
                  Thanks {form.name.split(" ")[0]} - the team will be in touch shortly. Want to skip the queue?
                </p>
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-calendly-button"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#ed4609] px-6 py-3 text-sm font-semibold text-white transition-shadow duration-200 hover:bg-[#ff571a] hover:shadow-[0_0_24px_rgba(237,70,9,0.45)]"
                >
                  Book your free strategy call <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <>
                <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]">Free Strategy Call</p>
                <h3 className="mt-2 text-2xl font-bold text-white">Let's map out your project.</h3>
                <p className="mt-1.5 text-sm text-[#a1a1aa]">
                  Tell us a little about you and we'll take it from there - no obligation.
                </p>
                <form onSubmit={submit} className="mt-5 space-y-3.5" data-testid="lead-form">
                  <input
                    required value={form.name} onChange={set("name")}
                    placeholder="Your name" data-testid="lead-name-input" aria-label="Your name"
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] transition-colors duration-200 focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <input
                    required type="email" value={form.email} onChange={set("email")}
                    placeholder="Email address" data-testid="lead-email-input" aria-label="Email address"
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] transition-colors duration-200 focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <input
                    value={form.company} onChange={set("company")}
                    placeholder="Company (optional)" data-testid="lead-company-input" aria-label="Company"
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] transition-colors duration-200 focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <select
                    value={form.interest} onChange={set("interest")}
                    data-testid="lead-interest-select" aria-label="What are you interested in?"
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white transition-colors duration-200 focus:border-[#ed4609]/60 focus:outline-none"
                  >
                    {INTERESTS.map((i) => <option key={i} value={i} className="bg-[#0a0a0c]">{i}</option>)}
                  </select>
                  <textarea
                    value={form.message} onChange={set("message")} rows={2}
                    placeholder="Anything we should know? (optional)" data-testid="lead-message-input" aria-label="Message"
                    className="w-full resize-none rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] transition-colors duration-200 focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit" disabled={busy}
                    data-testid="lead-submit-button"
                    className="w-full rounded-xl bg-[#ed4609] py-3.5 text-sm font-semibold text-white transition-shadow duration-200 hover:bg-[#ff571a] hover:shadow-[0_0_24px_rgba(237,70,9,0.45)] disabled:opacity-50"
                  >
                    {busy ? "Sending..." : "Request my strategy call"}
                  </motion.button>
                  <p className="text-center text-[10px] text-[#71717a]">
                    Protected by reCAPTCHA. We'll only use your details to follow up on this enquiry.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
